import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { CheckCircle, Calendar, Clock, ArrowLeft, Check } from 'lucide-react';
import { TRANSLATIONS } from '../data/mock';

const BookingScreen = ({ userInfo, results, language }) => {
  const t = TRANSLATIONS[language];
  const [showCalendar, setShowCalendar] = useState(true); // Afficher directement le calendrier
  const [bookingConfirmed, setBookingConfirmed] = useState(false);
  const [calendarUrl, setCalendarUrl] = useState('https://calendar.app.google/zpwrbwZCUGuKAUpr6');
  const [calendarDuration, setCalendarDuration] = useState(30);
  
  // Charger les paramètres du calendrier depuis le backend
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings`);
        const data = await response.json();
        if (data.success && data.settings) {
          setCalendarUrl(data.settings.calendar_url || 'https://calendar.app.google/zpwrbwZCUGuKAUpr6');
          setCalendarDuration(data.settings.calendar_duration || 20);
        }
      } catch (error) {
        console.error('Erreur lors du chargement des paramètres:', error);
      }
    };
    loadSettings();
  }, []);

  const handleBookingSuccess = () => {
    setBookingConfirmed(true);
    setShowCalendar(false);
  };

  // Écran de confirmation
  if (bookingConfirmed) {
    return (
      <div className="min-h-screen bg-[#0f1c47] p-4 flex items-center justify-center">
        <Card className="max-w-2xl mx-auto shadow-2xl border-0 bg-white/95 backdrop-blur-sm">
          <CardHeader className="text-center text-white relative overflow-hidden" style={{
            background: 'linear-gradient(135deg, #52a385 0%, #0f1c47 100%)'
          }}>
            <CardTitle className="text-2xl flex items-center justify-center gap-3">
              <Check className="w-8 h-8" />
              Réservation Confirmée !
            </CardTitle>
          </CardHeader>
          
          <CardContent className="p-8 space-y-6 text-center">
            <div className="w-20 h-20 mx-auto rounded-full bg-[#52a385]/10 flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-[#52a385]" />
            </div>
            
            <div className="space-y-2">
              <h3 className="text-xl font-semibold text-gray-800">
                Merci {userInfo?.firstName} !
              </h3>
              <p className="text-gray-600">
                Votre débrief stratégique est réservé. Vous recevrez un email de confirmation avec les détails du rendez-vous.
              </p>
            </div>

            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-500">
                Pendant cet échange, nous analyserons ensemble :
              </p>
              <ul className="mt-3 space-y-2 text-left max-w-sm mx-auto">
                <li className="flex items-start gap-2 text-gray-700 text-sm">
                  <CheckCircle className="w-4 h-4 text-[#52a385] mt-0.5 flex-shrink-0" />
                  Ce que révèle réellement votre score
                </li>
                <li className="flex items-start gap-2 text-gray-700 text-sm">
                  <CheckCircle className="w-4 h-4 text-[#52a385] mt-0.5 flex-shrink-0" />
                  Les mécanismes invisibles dans la dynamique d'équipe
                </li>
                <li className="flex items-start gap-2 text-gray-700 text-sm">
                  <CheckCircle className="w-4 h-4 text-[#52a385] mt-0.5 flex-shrink-0" />
                  Les leviers pour fluidifier l'exécution collective
                </li>
              </ul>
            </div>

            <p className="text-sm text-gray-500">
              WEDO Coaching & Conseil
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Écran de réservation avec calendrier
  return (
    <div className="min-h-screen bg-[#0f1c47] py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Logo */}
        <div className="text-center mb-6">
          <img 
            src="https://customer-assets.emergentagent.com/job_manager-inventory/artifacts/kqdhn43c_2024_10_make_it_matter.png" 
            alt="WEDO" 
            className="mx-auto h-14 object-contain"
          />
        </div>

        <Card className="bg-white/95 backdrop-blur-sm border-0 shadow-2xl">
          <CardHeader className="text-center pb-4">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#52a385] to-[#0f1c47] flex items-center justify-center">
                <Calendar className="w-8 h-8 text-white" />
              </div>
            </div>
            <CardTitle className="text-2xl font-bold text-[#0f1c47]">
              {t.debriefTitle || 'Recevoir votre débrief stratégique'}
            </CardTitle>
            <p className="text-gray-600 mt-2">
              {t.debriefText || 'Je propose un échange rapide pour analyser ce diagnostic.'}
            </p>
          </CardHeader>
          
          <CardContent className="space-y-6 px-6 md:px-10 pb-8">
            {/* Points de discussion */}
            <div className="bg-[#0f1c47]/5 rounded-lg p-4">
              <p className="text-sm text-gray-500 mb-3">Pendant cet échange nous regarderons :</p>
              <ul className="space-y-2">
                {(t.debriefPoints || [
                  "ce que révèle réellement votre score",
                  "les mécanismes invisibles dans la dynamique d'équipe",
                  "les leviers pour fluidifier l'exécution collective"
                ]).map((point, index) => (
                  <li key={index} className="flex items-start gap-3 text-gray-700">
                    <CheckCircle className="w-5 h-5 text-[#52a385] mt-0.5 flex-shrink-0" />
                    {point}
                  </li>
                ))}
              </ul>
            </div>

            {/* Durée */}
            <div className="flex items-center justify-center gap-2 text-[#e62c86] font-medium">
              <Clock className="w-5 h-5" />
              <span>Durée : {calendarDuration} minutes</span>
            </div>

            {/* Calendrier iframe */}
            <div className="relative w-full rounded-lg overflow-hidden border border-gray-200" style={{ height: '500px' }}>
              <iframe
                src={calendarUrl}
                className="w-full h-full"
                frameBorder="0"
                title="Réservation de débrief stratégique"
              ></iframe>
            </div>

            {/* Bouton de confirmation (optionnel) */}
            <div className="text-center pt-4">
              <Button
                onClick={handleBookingSuccess}
                variant="outline"
                className="text-[#52a385] border-[#52a385] hover:bg-[#52a385]/10"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                J'ai réservé mon créneau
              </Button>
            </div>

            {/* Info */}
            <p className="text-center text-sm text-gray-500">
              Offert • {calendarDuration} minutes • Sans engagement
            </p>
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-gray-400 text-sm mt-6">
          WEDO Coaching & Conseil
        </p>
      </div>
    </div>
  );
};

export default BookingScreen;
